/**
 * 탈퇴신청 처리창 — $j.F.Admin.Member.Leave
 *
 * **화면에 매이지 않는다.** 신청번호 하나면 어디서든 연다 —
 * 알림 모아보기(`/adm/tasks?type=leave`)가 연다.
 *
 *   $j.F.Admin.Member.Leave.config({ onChanged: reloadTable });   // 화면 1회
 *   $j.F.Admin.Member.Leave.open(num);                            // 열기
 *
 * 예전에는 blade 안에 `#leaveModal` 로 박혀 있었고, 어느 버튼을 눌렀는지(`mode`)를 호출부가
 * 정해 넘겼다. 그래서 그 화면 밖에서는 열 수 없었다. 지금은 **한 창에 두 동작**을 담는다 —
 * 부르는 쪽은 "이 신청을 처리한다" 만 알면 되고, 무엇을 할지는 창에서 고른다.
 *
 * 열기 전에 원장을 본다($j.F.Admin.TaskGuard) — 남이 붙잡고 있는 건을 모르고 겹쳐 처리하는
 * 것을 막는다. 짜고치기·악덕 처리창과 같은 문구·같은 규칙이다.
 *
 * **탈퇴 처리는 되돌려지지 않는 것이 있어서** 다른 창보다 문이 하나씩 더 있다 —
 * 「열람」으로 열거나 원장에서 닫힌 건이면 잠기고(lockOnView), 두 버튼 모두 확인을 한 번 더 받으며,
 * 메모는 처리와 따로 저장한다.
 */
export default class Leave {
    /** 원장에서 이 종류를 부르는 이름 */
    static TASK_TYPE = 'leave';

    constructor() {
        this.onChanged = null;
        this.handle    = null;
        this.row       = null;
    }

    /** @param {object} opts onChanged: 처리 뒤 목록을 다시 받게 하는 콜백 */
    config(opts = {}) {
        if (opts.onChanged) this.onChanged = opts.onChanged;
        this._bind();
        return this;
    }

    /** 신청 한 건을 연다. 인자는 신청번호 하나 — 원장이 아는 것이 그것뿐이다 */
    async open(num) {
        if (!await $j.F.Admin.TaskGuard.check(Leave.TASK_TYPE, num,
                { lockOnView: true, onClaimed: () => { if (this.onChanged) this.onChanged(); } })) return;

        let r;
        try {
            r = await $.post('/adm/ajax/member/leave/detail', { num });
        } catch (e) {
            $j.Ui.Toast.show('신청을 불러오지 못했습니다.', { type: 'warn' });
            return;
        }
        if (!r || !r.result) {
            $j.Ui.Toast.show((r && r.message) || '없는 신청입니다.', { type: 'warn' });
            return;
        }

        // 소스까지 끝난 건은 읽기만 하는 창이라(_body) 잠글 것이 없다
        if (Number(r.row.status) !== 1) $j.F.Admin.TaskGuard.clearViewOnly(Leave.TASK_TYPE, num);

        this._injectStyle();

        this.row = r.row;
        this.handle = $j.Ui.Modal.open({
            id: 'leave-case', width: '460px', variant: 'admin',
            title: `탈퇴신청 — ${this._esc(r.row.mem_name)}#${r.row.mb_no}`,
            html: this._body(r.row),
        });

        // 머리줄의 공통 칩 — 담당·상태·이력. 창마다 본문이 달라도 이 자리는 셋이 같다
        $j.F.Admin.TaskBadge.attach(this.handle.el, Leave.TASK_TYPE, num,
            { onChanged: () => { if (this.onChanged) this.onChanged(); } });
    }

    _body(d) {
        const done = Number(d.status) !== 1;   // 1=신청. 그 밖은 이미 처리된 것

        const rows = [
            ['신청일', this._esc((d.created_at || '').slice(0, 16))],
            ['탈퇴사유', this._esc(d.memo) || '<span class="text-muted">-</span>'],
            ['현재상태', this._esc(d.status_text)],
        ].map(([k, v]) => `<div class="lv-row"><span>${k}</span><b>${v}</b></div>`).join('');

        // 이미 처리된 건은 읽기만 — 되돌리려면 기사관리에서 한다
        const form = done ? '' : `
            <label class="lv-label" for="lv-memo">관리자 메모</label>
            <textarea id="lv-memo" class="form-control" rows="3"
                      placeholder="처리 사유·특이사항">${this._esc(d.admin_memo)}</textarea>
            <div class="lv-actions">
                <button type="button" class="btn btn-outline-secondary lv-actions__memo" data-lv-memo>메모 저장</button>
                <button type="button" class="btn btn-danger"  data-lv-act="leave">탈퇴 처리</button>
                <button type="button" class="btn btn-primary" data-lv-act="return">상태원복</button>
            </div>`;

        const note = done
            ? '<div class="lv-note">이미 처리된 신청입니다.</div>'
            : '';

        return `<div class="lv-case">${rows}${note}${form}</div>`;
    }

    /** 모달은 body 밑에 붙으므로 document 위임. 한 번만 건다 */
    _bind() {
        if (this._bound) return;
        this._bound = true;

        $(document).on('click.leavecase', '#leave-case [data-lv-act]', (e) => {
            this._act($(e.currentTarget).data('lv-act'));
        });
        $(document).on('click.leavecase', '#leave-case [data-lv-memo]', () => this._saveMemo());
    }

    /** 처리 전 확인 — 무엇이 어떻게 되는지 보여주고 한 번 더 묻는다 */
    _ask(mode) {
        const who = `${this._esc(this.row.mem_name)}#${this.row.mb_no}`;

        if (mode === 'return') {
            return $j.Ui.Dialog.open({
                maxWidth: 400,
                title:    `${who} 회원을 정상으로 되돌릴까요?`,
                desc:     '탈퇴신청이 「신청취소」로 닫힙니다.',
                hint:     '적어둘 내용만 있으면 「메모 저장」을 쓰세요.',
                confirm:  '상태원복',
                cancel:   '취소',
            });
        }
        return $j.Ui.Dialog.open({
            tone:     'danger',
            maxWidth: 400,
            title:    `${who} 회원을 탈퇴 처리할까요?`,
            desc:     '회원이 바로 탈퇴 상태가 됩니다.',
            notes:    ['기기·자동로그인 토큰, 소셜 연동, 본인확인(CI) 정보 삭제', '가상계좌 해지'],
            hint:     '상태원복으로 되돌려도 삭제된 정보는 복구되지 않습니다.',
            confirm:  '탈퇴 처리',
            cancel:   '취소',
        });
    }

    /** 메모만 저장 — 창은 그대로 두고 신청도 그대로다 */
    async _saveMemo() {
        const d = this.row;
        if (!d) return;

        const memo = $('#lv-memo').val() || '';
        const r = await $.post('/adm/ajax/member/leave/memo', { num: d.num, admin_memo: memo });
        if (!r || !r.result) {
            $j.Ui.Toast.show((r && r.message) || '저장하지 못했습니다.', { type: 'warn' });
            return;
        }

        d.admin_memo = memo;
        $j.Ui.Toast.show('메모를 저장했습니다.', { type: 'success' });
        if (this.onChanged) this.onChanged();
    }

    async _act(mode) {
        const d = this.row;
        if (!d || this._busy) return;

        if (!await this._ask(mode)) return;

        this._busy = true;
        let r = null;
        try {
            r = await $.post('/adm/ajax/member/leave/status', {
                num: d.num, mode,
                admin_memo: $('#lv-memo').val() || '',
            });
        } catch (e) {
            // 아래에서 안내한다
        } finally {
            this._busy = false;
        }

        if (!r || !r.result) {
            $j.Ui.Toast.show((r && r.message) || '처리하지 못했습니다.', { type: 'warn' });
            if (this.onChanged) this.onChanged();   // 남이 먼저 처리했으면 목록이 그 사실을 보여야 한다
            return;
        }

        $j.Ui.Toast.show(mode === 'return' ? '상태를 원복했습니다.' : '탈퇴 처리했습니다.', { type: 'success' });
        if (this.handle) this.handle.close();
        if (this.onChanged) this.onChanged();
    }

    /** 창 안쪽 모양. 모달은 body 밑에 붙으므로 페이지 CSS 에 둘 수 없다 — 1회만 주입한다 */
    _injectStyle() {
        if (document.getElementById('leave-case-style')) return;
        const el = document.createElement('style');
        el.id = 'leave-case-style';
        el.textContent = `
.lv-case .lv-row{display:flex;justify-content:space-between;gap:14px;
  padding:6px 2px;border-bottom:1px solid var(--adm-border);font-size:13px}
.lv-case .lv-row span{color:var(--adm-text-muted);flex:none}
.lv-case .lv-row b{font-weight:600;color:var(--adm-text-heading);text-align:right;word-break:break-all}
.lv-case .lv-label{display:block;margin:14px 0 5px;font-size:12px;color:var(--adm-text-muted)}
.lv-case .lv-note{margin-top:14px;font-size:12.5px;color:var(--adm-text-faint)}
.lv-case .lv-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:14px}
.lv-case .lv-actions__memo{margin-right:auto}`;
        document.head.appendChild(el);
    }

    _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
}
